import React, { useState } from "react";
import { Ticket, Sparkles, Users } from "lucide-react";
import TicketedEventForm from "../components/booking/TicketedEventForm";
import PrivateEventForm from "../components/booking/PrivateEventForm";
import CreativeCommunityForm from "../components/booking/CreativeCommunityForm";
import { PhotoWatermark } from "../components/PhotoElements";

const TABS = [
  {
    id: "ticketed",
    label: "Ticketed Events",
    blurb: "Stage plays, concerts, comedy, film screenings, festivals, spoken word.",
    icon: Ticket,
  },
  {
    id: "private",
    label: "Private Events",
    blurb: "Birthdays, receptions, dinners, mixers, meetings.",
    icon: Sparkles,
  },
  {
    id: "creative",
    label: "Creative & Community",
    blurb: "Workshops, rehearsals, readings, classes, community meetings.",
    icon: Users,
  },
];

export default function BookingInquiry() {
  const [activeTab, setActiveTab] = useState("ticketed");
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onSuccess = () => {
    setStatus({
      state: "success",
      msg: "Thank you! Your inquiry has been received. The Encore team will be in touch within 2–3 business days.",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const onError = (msg) => {
    setStatus({ state: "error", msg });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const resetStatus = () => setStatus({ state: "idle", msg: "" });

  const activeMeta = TABS.find((t) => t.id === activeTab);

  return (
    <div data-testid="booking-page" className="pt-32 pb-24 relative overflow-hidden">
      <PhotoWatermark index={0} position="right" opacity={0.1} />
      {/* HERO */}
      <section className="px-6 md:px-10 max-w-7xl mx-auto">
        <p className="font-overline text-xs text-[#DE9E48] mb-4">— Book Our Space</p>
        <h1 className="font-display text-6xl md:text-8xl tracking-tighter leading-[0.95]">
          Encore <span className="italic text-[#DE9E48]">Rental</span><br />
          Inquiry Form.
        </h1>
        <p className="font-body text-stone-300 mt-6 max-w-3xl text-lg leading-relaxed">
          Three ways to bring your event to Encore. Pick the form that best fits what you have in
          mind — our team will follow up within 2–3 business days.
        </p>
      </section>

      {/* TABS */}
      <section className="px-6 md:px-10 max-w-7xl mx-auto mt-14">
        <div
          role="tablist"
          aria-label="Booking inquiry type"
          data-testid="booking-tabs"
          className="grid sm:grid-cols-3 gap-3"
        >
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={active}
                data-testid={`booking-tab-${tab.id}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  resetStatus();
                }}
                className={[
                  "group text-left p-6 rounded-2xl border transition-all",
                  active
                    ? "bg-[#DE9E48]/10 border-[#DE9E48] shadow-[0_0_0_1px_#DE9E48]"
                    : "bg-stone-900/40 border-white/10 hover:border-[#DE9E48]/40",
                ].join(" ")}
              >
                <div className="flex items-start gap-3">
                  <Icon
                    size={22}
                    className={active ? "text-[#DE9E48]" : "text-stone-400 group-hover:text-[#DE9E48]"}
                  />
                  <div className="flex-1">
                    <p
                      className={[
                        "font-display text-xl tracking-tight leading-tight",
                        active ? "text-stone-50" : "text-stone-200",
                      ].join(" ")}
                    >
                      {tab.label}
                    </p>
                    <p className="font-body text-xs text-stone-400 leading-snug mt-1.5">
                      {tab.blurb}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* FORM AREA */}
      <section className="px-6 md:px-10 max-w-5xl mx-auto mt-12">
        {status.state === "success" ? (
          <div
            data-testid="booking-success"
            className="border border-[#DE9E48]/40 bg-[#DE9E48]/10 p-10 md:p-14 text-center rounded-2xl"
          >
            <p className="font-overline text-xs text-[#DE9E48] mb-4">— Inquiry Received</p>
            <h2 className="font-display text-4xl md:text-5xl mb-5">
              Thank <span className="italic text-[#DE9E48]">you.</span>
            </h2>
            <p className="font-body text-stone-300 max-w-xl mx-auto leading-relaxed">{status.msg}</p>
            <button
              data-testid="booking-new"
              onClick={resetStatus}
              className="btn-outline mt-10"
            >
              Submit Another Inquiry
            </button>
          </div>
        ) : (
          <div
            data-testid={`booking-panel-${activeTab}`}
            role="tabpanel"
            className="border border-white/10 bg-stone-900/40 p-8 md:p-12 rounded-2xl"
          >
            <div className="mb-10">
              <p className="font-overline text-[0.65rem] text-[#DE9E48] tracking-[0.2em] mb-3">
                — {activeMeta?.label}
              </p>
              <p className="font-body text-stone-400 text-sm leading-relaxed max-w-3xl">
                {activeMeta?.blurb} All fields marked with <span className="text-[#DE9E48]">*</span> are
                required.
              </p>
            </div>

            {status.state === "error" && (
              <div
                data-testid="booking-error"
                className="mb-8 border border-rose-700/40 bg-rose-700/10 p-4 rounded-xl"
              >
                <p className="font-overline text-[0.7rem] text-rose-400">× {status.msg}</p>
              </div>
            )}

            {activeTab === "ticketed" && (
              <TicketedEventForm onSuccess={onSuccess} onError={onError} />
            )}
            {activeTab === "private" && (
              <PrivateEventForm onSuccess={onSuccess} onError={onError} />
            )}
            {activeTab === "creative" && (
              <CreativeCommunityForm onSuccess={onSuccess} onError={onError} />
            )}
          </div>
        )}
      </section>
    </div>
  );
}
